/**
 * Feed Shuffle Engine
 *
 * Goals:
 * 1. Unseen items appear first (tracked via localStorage)
 * 2. Newer items have higher priority within the unseen bucket
 * 3. Session-random so each visit feels different
 * 4. No repeats until all items have been seen
 *
 * Algorithm: "Weighted Fisher-Yates with Seen Priority"
 * - Split items into [unseen, seen]
 * - Within each bucket, apply weight = freshness + session noise
 * - Sort by weight DESC, unseen bucket first
 * - When all seen, reshuffle everything (new session seed)
 */

const SEEN_KEY = "nitipdong:seenIds";
const SEEN_VERSION = 1; // bump to reset everyone's seen history
const MAX_SEEN_TRACK = 500; // prevent unbounded localStorage growth

/** Fisher-Yates shuffle (in-place, returns same array) */
function fisherYates(arr, rng) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Simple seeded PRNG (mulberry32) — deterministic per seed */
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Generate a session seed (per page load) */
function sessionSeed() {
  return Date.now() ^ Math.floor(Math.random() * 1e9);
}

/** Read seen IDs from localStorage → Set */
export function getSeenIds() {
  try {
    const raw = localStorage.getItem(`${SEEN_KEY}:v${SEEN_VERSION}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

/** Mark an item ID as seen */
export function markSeen(id) {
  if (!id) return;
  try {
    const seen = getSeenIds();
    seen.add(id);
    // Trim to MAX_SEEN_TRACK — remove oldest (FIFO-ish, since Set preserves insertion order)
    if (seen.size > MAX_SEEN_TRACK) {
      const arr = Array.from(seen);
      const trimmed = arr.slice(arr.length - MAX_SEEN_TRACK);
      localStorage.setItem(`${SEEN_KEY}:v${SEEN_VERSION}`, JSON.stringify(trimmed));
    } else {
      localStorage.setItem(`${SEEN_KEY}:v${SEEN_VERSION}`, JSON.stringify(Array.from(seen)));
    }
  } catch {
    // localStorage might be full or disabled — silently ignore
  }
}

/** Mark multiple IDs as seen */
export function markSeenBatch(ids) {
  if (!ids?.length) return;
  try {
    const seen = getSeenIds();
    for (const id of ids) seen.add(id);
    if (seen.size > MAX_SEEN_TRACK) {
      const arr = Array.from(seen);
      localStorage.setItem(
        `${SEEN_KEY}:v${SEEN_VERSION}`,
        JSON.stringify(arr.slice(arr.length - MAX_SEEN_TRACK))
      );
    } else {
      localStorage.setItem(`${SEEN_KEY}:v${SEEN_VERSION}`, JSON.stringify(Array.from(seen)));
    }
  } catch {
    // ignore
  }
}

/** Clear all seen history */
export function clearSeenHistory() {
  try {
    localStorage.removeItem(`${SEEN_KEY}:v${SEEN_VERSION}`);
  } catch {
    // ignore
  }
}

/**
 * Smart shuffle: unseen first (weighted by freshness), seen last.
 * Returns a NEW array — does not mutate input.
 *
 * @param {Array} items - products from /feed
 * @param {Object} opts
 * @param {number} opts.seed - optional seed for deterministic shuffle
 * @param {number} opts.freshnessWeight - how much to prioritize new items (default: 40)
 * @param {number} opts.noiseWeight - random noise range (default: 30)
 */
export function smartShuffle(items, opts = {}) {
  if (!items?.length) return [];

  const seen = getSeenIds();
  const seed = opts.seed ?? sessionSeed();
  const rng = mulberry32(seed);
  const fw = opts.freshnessWeight ?? 40;
  const nw = opts.noiseWeight ?? 30;

  // Parse created_at → age in days
  const now = Date.now();
  const scored = items.map((item) => {
    let ageDays = 30; // default old
    try {
      const created = new Date(item.created_at);
      if (!isNaN(created.getTime())) {
        ageDays = Math.max(0, (now - created.getTime()) / 86400000);
      }
    } catch {
      // keep default
    }

    // Freshness: 1.0 at 0 days → ~0.33 at 8 days → ~0.17 at 20 days
    const freshness = fw / (1 + ageDays / 4);
    // Session noise — different each load
    const noise = rng() * nw;
    const isSeen = seen.has(item.id);

    // Score: unseen get +100 bonus so they always come first
    const unseenBonus = isSeen ? 0 : 100;
    const score = unseenBonus + freshness + noise;

    return { item, score, isSeen, ageDays };
  });

  // Sort by score DESC
  scored.sort((a, b) => b.score - a.score);

  // Category diversity pass: no more than 2 same kategori consecutively
  const result = [];
  const deferred = [];
  let lastKat = "";
  let katRun = 0;

  for (const s of scored) {
    const kat = s.item.kategori || "Lainnya";
    if (kat === lastKat && katRun >= 2) {
      deferred.push(s);
    } else {
      result.push(s);
      if (kat === lastKat) katRun++;
      else {
        lastKat = kat;
        katRun = 1;
      }
    }
  }

  // Append any deferred items
  for (const s of deferred) result.push(s);

  return result.map((s) => s.item);
}

/**
 * Pick a random starting index for the feed.
 * Weighted toward 0–30% of the array so users usually start near the top
 * but not always at position 0.
 */
export function randomStartIndex(length) {
  if (length <= 1) return 0;
  // 70% chance: start in first 30% of items
  // 30% chance: start anywhere
  if (Math.random() < 0.7) {
    return Math.floor(Math.random() * Math.max(1, Math.floor(length * 0.3)));
  }
  return Math.floor(Math.random() * length);
}
